import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, User, Heart, ShoppingBag, Menu, X, ChevronDown, GitCompare, ChevronRight } from "lucide-react";
import { Show, UserButton } from "@clerk/react";
import { useI18n, LANGS } from "../lib/i18n";
import { useShop } from "../lib/store";
import { api, useFetch } from "../lib/api";
import { chf } from "../lib/format";
import { Logo } from "./Common";
import { Sheet, SheetContent, SheetTitle } from "./ui/sheet";

const MEGA = ["bedroom", "beds", "mattresses", "wardrobes", "living-room", "dining-room", "office", "storage", "home-accessories"];

const BED = ["modern-bedroom", "minimalist-bedroom", "small-apartment"];
const ROOMS_FOR = { bedroom: BED, beds: BED, mattresses: BED, wardrobes: BED, "living-room": ["scandinavian-living", "small-apartment"], "dining-room": ["contemporary-dining", "small-apartment"],
  office: ["home-office", "small-apartment"], storage: ["small-apartment", "home-office", "scandinavian-living"], default: ["modern-bedroom", "scandinavian-living", "contemporary-dining", "home-office", "small-apartment", "minimalist-bedroom"] };

const SearchBox = ({ onDone }) => {
  const { t, lang } = useI18n();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [res, setRes] = useState(null);
  const [recent, setRecent] = useState(() => JSON.parse(localStorage.getItem("hw_searches") || "[]"));
  const box = useRef(null);
  useEffect(() => {
    const id = setTimeout(() => api.get(`/search/suggest`, { params: { q, lang } }).then((r) => setRes(r.data)).catch(() => {}), 180);
    return () => clearTimeout(id);
  }, [q, lang]);
  useEffect(() => {
    const h = (e) => box.current && !box.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const go = (term) => {
    if (!term.trim()) return;
    const next = [term, ...recent.filter((x) => x !== term)].slice(0, 5);
    setRecent(next); localStorage.setItem("hw_searches", JSON.stringify(next));
    setOpen(false); onDone?.();
    nav(`/search?q=${encodeURIComponent(term)}`);
  };
  return (
    <div ref={box} className="relative w-full">
      <form role="search" onSubmit={(e) => { e.preventDefault(); go(q); }} className="flex items-center h-11 border border-[color:var(--hw-line)] bg-white rounded-sm focus-within:border-[color:var(--hw-ink)]">
        <Search size={17} className="ml-3 text-[color:var(--hw-muted)]" />
        <input value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} onKeyDown={(e) => e.key === "Escape" && setOpen(false)}
          placeholder={t("search_ph")} aria-label={t("search_ph")} className="flex-1 h-full px-3 text-sm bg-transparent outline-none" data-testid="header-search-input" />
      </form>
      {open && res && (
        <div className="absolute z-50 top-12 left-0 right-0 bg-white border border-[color:var(--hw-line)] shadow-xl p-4 grid gap-4" data-testid="search-autocomplete-list">
          {q.length < 2 ? (
            <div className="grid sm:grid-cols-2 gap-4">
              {recent.length > 0 && <div><p className="eyebrow mb-2">{t("recent_searches")}</p>{recent.map((r) => <button key={r} onClick={() => go(r)} className="block text-sm py-1 hover:underline">{r}</button>)}</div>}
              <div><p className="eyebrow mb-2">{t("popular_searches")}</p>{res.popular.map((r) => <button key={r} onClick={() => go(r)} className="block text-sm py-1 hover:underline" data-testid="popular-search-item">{r}</button>)}</div>
            </div>
          ) : (
            <>
              {res.categories.length > 0 && <div><p className="eyebrow mb-2">{t("categories")}</p><div className="flex flex-wrap gap-2">{res.categories.map((c) => <Link key={c.slug} to={`/c/${c.slug}`} onClick={() => { setOpen(false); onDone?.(); }} className="text-sm px-3 py-1 bg-[color:var(--hw-beige)] hover:bg-[color:var(--hw-line)]">{c.name}</Link>)}</div></div>}
              <div>
                <p className="eyebrow mb-2">{t("products")}</p>
                {res.products.length === 0 && <p className="text-sm text-[color:var(--hw-muted)]">{t("no_results")}</p>}
                {res.products.map((p) => (
                  <Link key={p.id} to={`/p/${p.slug}`} onClick={() => { setOpen(false); onDone?.(); }} className="flex items-center gap-3 py-2 hover:bg-[color:var(--hw-bg)]" data-testid={`search-suggestion-${p.slug}`}>
                    <img src={p.image} alt="" className="w-12 h-14 object-cover" loading="lazy" />
                    <span className="flex-1 text-sm">{p.name}<span className="block text-xs text-[color:var(--hw-muted)]">{p.sku}</span></span>
                    <span className="text-sm font-semibold">{chf(p.price)}</span>
                  </Link>
                ))}
              </div>
              <button onClick={() => go(q)} className="text-sm font-semibold link-u text-left" data-testid="search-see-all">{t("view_all")} →</button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const MegaPanel = ({ cat, cats, onClose }) => {
  const { t, lang } = useI18n();
  const { data } = useFetch(`/products?category=${cat.slug}&sort=bestsellers&limit=3&lang=${lang}`, { cacheable: true });
  const subs = useMemo(() => cats.filter((c) => c.parent === cat.slug), [cats, cat.slug]);
  const grand = (s) => cats.filter((c) => c.parent === s);
  const { data: cols } = useFetch(`/collections?lang=${lang}`, { cacheable: true });
  const rooms = useMemo(() => (cols || []).filter((c) => c.kind === "room" && (ROOMS_FOR[cat.slug] || ROOMS_FOR.default).includes(c.slug)), [cols, cat.slug]);
  return (
    <div className="absolute left-0 right-0 top-full bg-white border-y border-[color:var(--hw-line)] shadow-lg z-40" data-testid={`mega-menu-${cat.slug}`}>
      <div className="container-hw grid grid-cols-12 gap-8 py-8">
        <div className="col-span-3">
          <p className="eyebrow mb-3">{t("subcategories")}</p>
          <Link to={`/c/${cat.slug}`} onClick={onClose} className="block font-semibold text-sm py-1.5 hover:underline">{t("view_all")} – {cat.name}</Link>
          {subs.map((s) => (
            <div key={s.slug}>
              <Link to={`/c/${s.slug}`} onClick={onClose} className="block text-sm py-1.5 hover:underline">{s.name}</Link>
              {grand(s.slug).map((g) => <Link key={g.slug} to={`/c/${g.slug}`} onClick={onClose} className="block text-xs pl-3 py-1 text-[color:var(--hw-muted)] hover:underline">{g.name}</Link>)}
            </div>
          ))}
        </div>
        <div className="col-span-2">
          <p className="eyebrow mb-3">{t("collections")}</p>
          {rooms.map((c) => <Link key={c.slug} to={`/room/${c.slug}`} onClick={onClose} className="block text-sm py-1.5 hover:underline">{c.name}</Link>)}
        </div>
        <div className="col-span-4">
          <p className="eyebrow mb-3">{t("popular")}</p>
          <div className="grid grid-cols-3 gap-3">
            {(data?.items || []).map((p) => (
              <Link key={p.id} to={`/p/${p.slug}`} onClick={onClose} className="group">
                <div className="aspect-[4/5] overflow-hidden bg-[color:var(--hw-beige)]"><img src={p.image} alt={p.name} loading="lazy" className="w-full h-full object-cover img-zoom" /></div>
                <p className="text-xs mt-2 line-clamp-2">{p.name}</p><p className="text-xs font-semibold">{chf(p.price)}</p>
              </Link>
            ))}
          </div>
        </div>
        <Link to="/sale" onClick={onClose} className="col-span-3 relative overflow-hidden group block min-h-[220px]">
          <img src={cat.image} alt={cat.name} className="absolute inset-0 w-full h-full object-cover img-zoom" loading="lazy" />
          <div className="absolute inset-0 bg-black/35" />
          <div className="absolute bottom-0 p-5 text-white"><p className="eyebrow !text-white/80">{t("special_offers")}</p><p className="font-serif text-2xl">{t("nav_sale")} – {cat.name}</p></div>
        </Link>
      </div>
    </div>
  );
};

const MobileNav = ({ open, setOpen, cats }) => {
  const { t, lang, setLang } = useI18n();
  const [exp, setExp] = useState(null);
  const close = () => setOpen(false);
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="left" className="w-[88vw] max-w-sm p-0 overflow-y-auto" data-testid="mobile-nav-drawer">
        <SheetTitle className="p-5 border-b"><Logo size="h-12" /></SheetTitle>
        <div className="p-4"><SearchBox onDone={close} /></div>
        <nav className="px-2 pb-6">
          {[["/new", t("nav_new")], ["/bestsellers", t("nav_best")], ["/sale", t("nav_sale")]].map(([to, l]) => <Link key={to} to={to} onClick={close} className="block px-3 py-3 text-sm font-semibold border-b">{l}</Link>)}
          {MEGA.map((s) => cats.find((c) => c.slug === s)).filter(Boolean).map((c) => (
            <div key={c.slug} className="border-b">
              <button className="w-full flex justify-between items-center px-3 py-3 text-sm" aria-expanded={exp === c.slug} onClick={() => setExp(exp === c.slug ? null : c.slug)} data-testid={`mobile-nav-${c.slug}`}>
                {c.name}<ChevronDown size={16} className={exp === c.slug ? "rotate-180" : ""} />
              </button>
              {exp === c.slug && <div className="pb-3 pl-6">
                <Link to={`/c/${c.slug}`} onClick={close} className="block py-1.5 text-sm font-semibold">{t("view_all")}</Link>
                {cats.filter((x) => x.parent === c.slug).map((x) => <Link key={x.slug} to={`/c/${x.slug}`} onClick={close} className="block py-1.5 text-sm">{x.name}</Link>)}
              </div>}
            </div>
          ))}
          <Link to="/inspiration" onClick={close} className="block px-3 py-3 text-sm border-b">{t("nav_inspiration")}</Link>
          <div className="flex gap-2 px-3 pt-5">{LANGS.map((l) => <button key={l} onClick={() => setLang(l)} className={`px-3 py-1.5 text-xs uppercase border ${l === lang ? "bg-[color:var(--hw-ink)] text-white" : ""}`}>{l}</button>)}</div>
        </nav>
      </SheetContent>
    </Sheet>
  );
};

export default function Header({ settings }) {
  const { t, lang, setLang, currency } = useI18n();
  const { cartCount, wishlist, compare, me } = useShop();
  const { data: cats = [] } = useFetch(`/categories?lang=${lang}`, { cacheable: true });
  const [mega, setMega] = useState(null);
  const [mobile, setMobile] = useState(false);
  const timer = useRef(null);
  const enter = (s) => { clearTimeout(timer.current); timer.current = setTimeout(() => setMega(s), 120); };
  const leave = () => { clearTimeout(timer.current); timer.current = setTimeout(() => setMega(null), 150); };
  const top = (s) => (cats || []).find((c) => c.slug === s);
  const Icon = ({ to, label, count, children, testid }) => (
    <Link to={to} aria-label={label} className="relative w-10 h-10 grid place-items-center hover:bg-[color:var(--hw-beige)] rounded-sm" data-testid={testid}>
      {children}{count > 0 && <span className="absolute top-0.5 right-0.5 min-w-[18px] h-[18px] px-1 text-[10px] font-bold grid place-items-center bg-[color:var(--hw-green)] text-white rounded-full" data-testid={`${testid}-count`}>{count}</span>}
    </Link>
  );
  return (
    <header className="sticky top-0 z-40 bg-[color:var(--hw-bg)]/95 backdrop-blur-md border-b border-[color:var(--hw-line)]" onKeyDown={(e) => e.key === "Escape" && setMega(null)}>
      {settings?.announcement?.active && <div className="bg-[color:var(--hw-olive)] text-white text-xs text-center py-2 px-4" data-testid="announcement-bar">{settings.announcement.text}</div>}
      <div className="container-hw flex items-center gap-3 lg:gap-8 h-16 lg:h-[72px]">
        <button className="lg:hidden w-10 h-10 grid place-items-center" aria-label={t("menu")} onClick={() => setMobile(true)} data-testid="mobile-menu-button"><Menu size={22} /></button>
        <Link to="/" aria-label="ANEL Furniture" data-testid="header-logo-link"><Logo /></Link>
        <div className="hidden lg:block flex-1 max-w-2xl"><SearchBox /></div>
        <div className="flex items-center gap-1 ml-auto">
          <select value={lang} onChange={(e) => setLang(e.target.value)} aria-label={t("language")} className="hidden sm:block h-9 bg-transparent text-xs font-semibold uppercase px-1 cursor-pointer" data-testid="language-switcher-select">
            {LANGS.map((l) => <option key={l} value={l}>{l.toUpperCase()}</option>)}
          </select>
          <span className="hidden sm:inline text-xs font-semibold text-[color:var(--hw-muted)] px-2 border-l" data-testid="currency-indicator">{currency}</span>
          {["admin", "catalog_manager", "order_manager"].includes(me?.role) && <Link to="/admin" className="hidden md:inline text-xs font-semibold px-2 link-u" data-testid="header-admin-link">{t("admin")}</Link>}
          <Show when="signed-out"><Icon to="/sign-in" label={t("sign_in")} testid="header-account-link"><User size={20} /></Icon></Show>
          <Show when="signed-in"><Icon to="/account" label={t("account")} testid="header-account-link"><User size={20} /></Icon><span className="hidden md:block ml-1"><UserButton /></span></Show>
          <Icon to="/compare" label={t("compare")} count={compare.length} testid="header-compare-link"><GitCompare size={19} /></Icon>
          <Icon to="/wishlist" label={t("wishlist")} count={wishlist.length} testid="header-wishlist-link"><Heart size={20} /></Icon>
          <Icon to="/cart" label={t("cart")} count={cartCount} testid="cart-drawer-trigger"><ShoppingBag size={20} /></Icon>
        </div>
      </div>
      <div className="lg:hidden container-hw pb-3"><SearchBox /></div>
      <nav className="hidden lg:block relative border-t border-[color:var(--hw-line)]" aria-label="Main" onMouseLeave={leave}>
        <ul className="container-hw flex items-center gap-6 h-11 text-[13px] font-medium overflow-x-auto no-scrollbar">
          <li><Link to="/new" className="hover:underline" data-testid="nav-new">{t("nav_new")}</Link></li>
          <li><Link to="/bestsellers" className="hover:underline" data-testid="nav-bestsellers">{t("nav_best")}</Link></li>
          <li><Link to="/sale" className="text-[color:var(--hw-sale)] font-semibold hover:underline" data-testid="nav-sale">{t("nav_sale")}</Link></li>
          {MEGA.map((s) => top(s) && (
            <li key={s} onMouseEnter={() => enter(s)} className="h-full flex items-center">
              <Link to={`/c/${s}`} className={`h-full flex items-center border-b-2 ${mega === s ? "border-[color:var(--hw-ink)]" : "border-transparent"}`} data-testid={`nav-${s}`}
                aria-expanded={mega === s} aria-haspopup="true" onFocus={() => setMega(s)}>{top(s).name}</Link>
              <button className="sr-only focus:not-sr-only" aria-label={`${top(s).name} menu`} onClick={() => setMega(mega === s ? null : s)}><ChevronRight size={12} /></button>
            </li>
          ))}
          <li><Link to="/inspiration" className="hover:underline" data-testid="nav-inspiration">{t("nav_inspiration")}</Link></li>
        </ul>
        {mega && top(mega) && <div onMouseEnter={() => clearTimeout(timer.current)}><MegaPanel cat={top(mega)} cats={cats} onClose={() => setMega(null)} /></div>}
      </nav>
      <MobileNav open={mobile} setOpen={setMobile} cats={cats || []} />
      {mobile && <span className="hidden"><X /></span>}
    </header>
  );
}
